/**
 * Edge integrity signals (TI-735) — on-device, per-frame, privacy-preserving.
 *
 * MediaPipe Tasks-Vision FaceDetector runs entirely in the browser (frames never leave
 * the device) to compute the cheap continuous signals — face presence + face count —
 * so most checks need no server round-trip (audit §4). The heavy identity match stays a
 * periodic server call.
 *
 * MediaPipe is loaded from the jsDelivr CDN via a `webpackIgnore` dynamic import so it is
 * NOT bundled (no build-time wasm handling) and stays a runtime-only, fail-open dependency:
 * if the CDN/model can't load, edge signals report "unavailable" and the server path still
 * runs. The model + wasm fetch from CDN at runtime only.
 */

const VISION_CDN = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18";
const WASM_ROOT = `${VISION_CDN}/wasm`;
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";

const UNAVAILABLE = {
  available: false,
  faceCount: 0,
  present: false,
  multiple: false,
  lookingAway: false,
};

// |nose offset from the eye-midpoint, in inter-eye widths| beyond this == head turned away.
// Cheap gaze/head-pose proxy from the detector's keypoints — no second (FaceLandmarker)
// model download. Conservative so a glance doesn't trip it (flag-not-block).
const GAZE_OFFSET_RATIO = 0.6;
// Face bbox centre this close to either horizontal edge (fraction of frame) == off to a side.
const OFF_SCREEN_MARGIN = 0.2;

// Substrings (lower-cased) marking a virtual / injected camera (OBS, Deep-Live-Cam over a
// virtual cam, Snap, etc.). enumerateDevices labels are only populated after camera
// permission is granted — which the interview already has.
const VIRTUAL_CAM_MARKERS = [
  "obs",
  "virtual",
  "manycam",
  "xsplit",
  "snap camera",
  "snapcam",
  "droidcam",
  "epoccam",
  "ndi",
  "camtwist",
  "fake",
  "avatarify",
];

/**
 * Head-pose / off-screen proxy from a single detection's keypoints + box.
 * blaze_face_short_range keypoints: [rightEye, leftEye, noseTip, mouth, rightEar, leftEar]
 * (normalised 0..1). Returns true when the candidate is looking away / off-screen.
 */
function isLookingAway(detection) {
  try {
    const kp = detection && detection.keypoints;
    if (!kp || kp.length < 3) return false;
    const [rightEye, leftEye, nose] = kp;
    const eyeMidX = (rightEye.x + leftEye.x) / 2;
    const eyeDist = Math.abs(leftEye.x - rightEye.x);
    // Nose offset from the eye-midpoint, in inter-eye widths → head turned left/right.
    if (eyeDist > 0 && Math.abs(nose.x - eyeMidX) / eyeDist > GAZE_OFFSET_RATIO) return true;
    // Nose horizontally near the frame edge → looking/leaning off-screen.
    return nose.x < OFF_SCREEN_MARGIN || nose.x > 1 - OFF_SCREEN_MARGIN;
  } catch (_) {
    return false;
  }
}

/**
 * One-shot scan of the candidate's media devices for a virtual / injected camera (TI-735).
 * Best-effort: returns { virtual:false } when the API is unavailable or labels are empty.
 */
export async function detectVirtualCamera() {
  try {
    if (!navigator?.mediaDevices?.enumerateDevices) return { virtual: false, label: null };
    const devices = await navigator.mediaDevices.enumerateDevices();
    for (const d of devices) {
      if (d.kind !== "videoinput" || !d.label) continue;
      const label = d.label.toLowerCase();
      if (VIRTUAL_CAM_MARKERS.some((m) => label.includes(m))) {
        return { virtual: true, label: d.label };
      }
    }
    return { virtual: false, label: null };
  } catch (_) {
    return { virtual: false, label: null };
  }
}

function noopDetector() {
  return {
    available: false,
    detect() {
      return UNAVAILABLE;
    },
    dispose() {},
  };
}

export async function createEdgeDetector() {
  try {
    const vision = await import(/* webpackIgnore: true */ `${VISION_CDN}/vision_bundle.mjs`);
    const { FilesetResolver, FaceDetector } = vision;
    const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
    const detector = await FaceDetector.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL },
      runningMode: "VIDEO",
      minDetectionConfidence: 0.5,
    });

    return {
      available: true,
      detect(videoEl, timestampMs) {
        try {
          const res = detector.detectForVideo(videoEl, timestampMs);
          const detections = (res && res.detections) || [];
          const faceCount = detections.length;
          return {
            available: true,
            faceCount,
            present: faceCount >= 1,
            multiple: faceCount > 1,
            // Only meaningful with exactly one face (multi-face is its own signal).
            lookingAway: faceCount === 1 && isLookingAway(detections[0]),
          };
        } catch (_) {
          return UNAVAILABLE;
        }
      },
      dispose() {
        try {
          detector.close();
        } catch (_) {
          /* best-effort */
        }
      },
    };
  } catch (_) {
    // CDN/load failure → fail open.
    return noopDetector();
  }
}
